import React, { Component } from 'react';
import PropTypes from 'prop-types';

/**
 * Per-section error boundary. A throw inside ONE section (e.g. equipment reading
 * an order field that is absent for this order) degrades to a compact inline
 * notice for just that section, so the REST of the step still renders and stays
 * usable instead of the whole-form boundary taking the entire step down.
 *
 * The raw message is shown only OUTSIDE production; now that the engine is the
 * default path this is user-facing, so production shows a generic notice and the
 * detail goes to the console.
 */
const isDev = process.env.NODE_ENV !== 'production';
class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const { name } = this.props;
    // eslint-disable-next-line no-console
    console.error(`[forms-core] section "${name}" failed:`, error, info);
  }

  render() {
    const { error } = this.state;
    const { name, children } = this.props;
    if (!error) return children;
    return (
      <div
        role="note"
        style={{
          margin: '0.5rem 0',
          padding: '0.5rem 0.75rem',
          border: '1px dashed #c12335',
          borderRadius: '4px',
          background: '#fff5f6',
          color: '#57000a',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}
      >
        This section could not be displayed.
        {isDev ? (
          <>
            {' '}
            [{name}] {(error && error.message) || String(error)}
          </>
        ) : null}
      </div>
    );
  }
}

SectionErrorBoundary.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.node
};
SectionErrorBoundary.defaultProps = { children: null };

/**
 * Wrap every component in a `sectionRegistry` map with a `SectionErrorBoundary`,
 * keyed by its registry name. Transparent when nothing throws.
 *
 * @param {Object.<string, import('react').ComponentType<*>>} registry
 * @returns {Object.<string, import('react').ComponentType<*>>}
 */
export const withSectionBoundaries = registry =>
  Object.keys(registry || {}).reduce((wrapped, key) => {
    const Section = registry[key];
    const Guarded = props => (
      <SectionErrorBoundary name={key}>
        <Section {...props} />
      </SectionErrorBoundary>
    );
    Guarded.displayName = `Guarded(${key})`;
    return { ...wrapped, [key]: Guarded };
  }, {});

export default SectionErrorBoundary;
